import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import {
  constants,
  useAccountNumbersField,
  useAccountNumbersState,
  useAccountNumbersValidation,
  useOutsideClick,
} from '@atlas/core';
import { Errors, Icon, Input, Label, Loader } from '@atlas/ui';

const { ACCOUNT_NUMBERS_FIELDS } = constants;

const VALIDATION_ERROR = 'CHOOSE_BANK';

const Option = ({ errors, fieldIndex, fieldName, ispb, name, setIsOpen }) => {
  const { removeFieldError, setFieldValue } = useAccountNumbersState();

  const onClickHandler = useCallback(() => {
    const hasLengthError = errors.some(({ type }) => type === VALIDATION_ERROR);

    if (hasLengthError) {
      removeFieldError({ fieldIndex, name: fieldName, type: VALIDATION_ERROR });
    }

    setFieldValue({ fieldIndex, name: ACCOUNT_NUMBERS_FIELDS.BANK_CODE, value: ispb || '' });
    setFieldValue({ fieldIndex, name: fieldName, value: name || '' });
    setIsOpen(false);
  }, [errors, setFieldValue, fieldIndex, ispb, fieldName, name, setIsOpen, removeFieldError]);

  return (
    <button className="combobox-option" onClick={onClickHandler} type="button">
      {name}
    </button>
  );
};

const Options = ({ errors, fieldIndex, fieldName, options, setIsOpen }) => {
  if (!options.length) {
    return <Label className="empty-state" message="no_banks_found" />;
  }

  return (
    <Virtuoso
      data={options}
      itemContent={(_, { code, ispb, name }) => (
        <Option
          errors={errors}
          fieldIndex={fieldIndex}
          fieldName={fieldName}
          ispb={ispb}
          key={`${ispb}-${code}`}
          name={name}
          setIsOpen={setIsOpen}
        />
      )}
      style={{ height: '200px' }}
      totalCount={options.length}
    />
  );
};

const BankNameCombobox = ({ fieldIndex, hint, name, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const { appendFieldError, banks, banksLoading, setFieldValue } = useAccountNumbersState();
  const { errors, isActive, isError, isTouched, label, onBlur, onChange, onFocus, value } =
    useAccountNumbersField({
      fieldIndex,
      name,
    });

  const { value: bankCode } = useAccountNumbersField({
    fieldIndex,
    name: ACCOUNT_NUMBERS_FIELDS.BANK_CODE,
  });

  const filteredBanks = useMemo(() => {
    return banks.filter(({ name }) => name.toLowerCase().includes(value.toLowerCase()));
  }, [banks, value]);

  const onFocusHandler = () => {
    setIsOpen(true);
    onFocus();
  };

  const onClickOutsideHandler = useCallback(() => {
    if (!filteredBanks.length) {
      setFieldValue({ fieldIndex, name, value: '' });
    }

    const bankNames = banks.map(({ name }) => name);
    const hasLengthError = errors.some(({ type }) => type === VALIDATION_ERROR);

    if (!bankNames.includes(value) || !bankCode) {
      if (hasLengthError) {
        setIsOpen(false);
        return;
      }

      appendFieldError({
        error: {
          message: 'choose_bank',
          type: VALIDATION_ERROR,
        },
        fieldIndex,
        name,
      });
    }

    setIsOpen(false);
    onBlur();
  }, [
    filteredBanks.length,
    banks,
    errors,
    value,
    bankCode,
    onBlur,
    setFieldValue,
    fieldIndex,
    name,
    appendFieldError,
  ]);

  useOutsideClick(ref, onClickOutsideHandler);
  useAccountNumbersValidation({ fieldIndex, isTouched, name });

  return (
    <div className="account-number-form__inner required">
      <div className="combobox" ref={ref}>
        <Input
          active={isActive}
          disabled={readOnly}
          error={isError}
          label={label}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocusHandler}
          value={value}
        />
        <Icon name={isOpen ? 'arrow-up' : 'arrow-down'} onClick={onFocusHandler} />
        {isOpen && (
          <div className="dropdown-list">
            {banksLoading ? (
              <Loader className="combobox-loader" />
            ) : (
              <Options
                errors={errors}
                fieldIndex={fieldIndex}
                fieldName={name}
                loading={banksLoading}
                options={filteredBanks}
                setIsOpen={setIsOpen}
              />
            )}
          </div>
        )}
      </div>

      {isError ? (
        <Errors errors={errors.map(({ message, params }) => ({ message, params }))} />
      ) : (
        <Label className="hint" message={hint} />
      )}
    </div>
  );
};

export default BankNameCombobox;